import { useMemo, useRef, useState } from 'react';
import {
  SUBTITLE_INLINE_STYLE_PRESET_IDS,
  getSubtitleInlinePresetStyle,
  resolveSubtitleInlineFontScale,
  resolveSubtitleInlineStyle,
  type SubtitleInlineStylePresetId,
  type SubtitleStyleSpan,
} from '@packages/global';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

interface SubtitleKeywordStylerProps {
  value: string;
  spans: SubtitleStyleSpan[];
  onTextChange: (text: string) => void;
  onSpansChange: (spans: SubtitleStyleSpan[]) => void;
}

function normalizeSpansForText(spans: SubtitleStyleSpan[], text: string) {
  return spans
    .map((span) => ({
      ...span,
      start: Math.max(0, Math.min(text.length, span.start)),
      end: Math.max(0, Math.min(text.length, span.end)),
    }))
    .filter((span) => span.end > span.start);
}

export default function SubtitleKeywordStyler({
  value,
  spans,
  onTextChange,
  onSpansChange,
}: SubtitleKeywordStylerProps) {
  const { t } = useTranslation(['editor', 'common']);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [selection, setSelection] = useState<{ start: number; end: number } | null>(null);
  const baseFontSize = 18;

  const normalizedSpans = useMemo(
    () => normalizeSpansForText(spans, value),
    [spans, value],
  );

  const selectedText = useMemo(() => {
    if (!selection || selection.end <= selection.start) return '';
    return value.slice(selection.start, selection.end);
  }, [selection, value]);

  const handleSelectionCapture = () => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    setSelection(start === end ? null : { start, end });
  };

  const handleApplyPreset = (presetId: SubtitleInlineStylePresetId) => {
    if (!selection || selection.end <= selection.start) return;

    const existingExactSpan = normalizedSpans.find(
      (span) => span.start === selection.start && span.end === selection.end,
    );
    const nextSpans = normalizedSpans.filter((span) => span.id !== existingExactSpan?.id)
      .filter((span) => span.end <= selection.start || span.start >= selection.end);

    nextSpans.push({
      id: existingExactSpan?.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      start: selection.start,
      end: selection.end,
      style: {
        ...(existingExactSpan?.style || {}),
        ...getSubtitleInlinePresetStyle(presetId),
        presetId,
      },
    });

    onSpansChange(nextSpans.sort((a, b) => a.start - b.start));
  };

  const handleApplyFontSize = (fontSize?: number) => {
    if (!selection || selection.end <= selection.start) return;

    const existingExactSpan = normalizedSpans.find(
      (span) => span.start === selection.start && span.end === selection.end,
    );
    const nextSpans = normalizedSpans.filter((span) => span.id !== existingExactSpan?.id)
      .filter((span) => span.end <= selection.start || span.start >= selection.end);

    const nextStyle = {
      ...(existingExactSpan?.style || {}),
      fontSize,
      fontScale: fontSize ? Number((fontSize / baseFontSize).toFixed(3)) : undefined,
    };

    const shouldKeepSpan = Object.values(nextStyle).some((item) => item !== undefined && item !== false && item !== '');
    if (shouldKeepSpan) {
      nextSpans.push({
        id: existingExactSpan?.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        start: selection.start,
        end: selection.end,
        style: nextStyle,
      });
    }

    onSpansChange(nextSpans.sort((a, b) => a.start - b.start));
  };

  const handleDeleteSpan = (spanId: string) => {
    onSpansChange(normalizedSpans.filter((span) => span.id !== spanId));
  };

  const handleUpdateSpanFontSize = (spanId: string, fontSize?: number) => {
    onSpansChange(
      normalizedSpans.map((span) => (
        span.id === spanId
          ? {
            ...span,
            style: {
              ...span.style,
              fontSize,
              fontScale: fontSize ? Number((fontSize / baseFontSize).toFixed(3)) : undefined,
            },
          }
          : span
      )),
    );
  };

  const handleTextUpdate = (text: string) => {
    onTextChange(text);
    onSpansChange(normalizeSpansForText(normalizedSpans, text));
  };

  return (
    <div className="space-y-3">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => handleTextUpdate(e.target.value)}
        onSelect={handleSelectionCapture}
        onKeyUp={handleSelectionCapture}
        onMouseUp={handleSelectionCapture}
        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
        rows={3}
        placeholder={t('editor:storyboardTab.dialogueScriptPlaceholder')}
      />

      <div className="rounded-lg border border-gray-700 bg-gray-800/70 p-3 space-y-3">
        <div className="text-xs text-gray-400">
          {selectedText
            ? t('editor:storyboardTab.keywordSelected', { text: selectedText })
            : t('editor:storyboardTab.keywordHint')}
        </div>

        <div className="grid grid-cols-5 gap-2">
          {SUBTITLE_INLINE_STYLE_PRESET_IDS.map((presetId) => {
            const presetStyle = resolveSubtitleInlineStyle({ presetId, ...getSubtitleInlinePresetStyle(presetId) });
            return (
              <button
                key={presetId}
                type="button"
                disabled={!selectedText}
                onClick={() => handleApplyPreset(presetId)}
                title={t(`editor:storyboardTab.inlinePresets.${presetId}`)}
                className="h-12 rounded-lg bg-gray-700 border border-transparent hover:border-gray-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <span
                  className="text-lg font-bold"
                  style={{
                    color: presetStyle?.color || '#ffffff',
                    textShadow: '0.6px 0.6px 0 rgba(0,0,0,0.55), -0.6px -0.6px 0 rgba(0,0,0,0.55)',
                  }}
                >
                  字幕
                </span>
              </button>
            );
          })}
        </div>

        {normalizedSpans.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs text-gray-400">{t('editor:storyboardTab.keywordEffects')}</div>
            {normalizedSpans.map((span) => {
              const text = value.slice(span.start, span.end);
              const resolvedStyle = resolveSubtitleInlineStyle(span.style);
              const resolvedScale = resolveSubtitleInlineFontScale(resolvedStyle, baseFontSize);
              return (
                <div
                  key={span.id}
                  className="flex items-center justify-between rounded-lg bg-gray-700/70 px-3 py-2 text-sm gap-3"
                >
                    <span
                      className="font-medium min-w-0"
                      style={{
                        color: resolvedStyle?.color || '#fff',
                        fontSize: resolvedScale
                          ? `${Math.round(baseFontSize * resolvedScale)}px`
                          : resolvedStyle?.fontSize ? `${resolvedStyle.fontSize}px` : undefined,
                        fontWeight: resolvedStyle?.bold ? 700 : undefined,
                      }}
                  >
                    {text}
                  </span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleUpdateSpanFontSize(span.id, baseFontSize + 8)}
                      className="rounded-md bg-gray-600 px-2.5 py-1 text-xs font-semibold text-gray-100 hover:bg-gray-500 transition-colors"
                    >
                      {t('editor:storyboardTab.inlineActions.sizeUp')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUpdateSpanFontSize(span.id, baseFontSize + 16)}
                      className="rounded-md bg-gray-600 px-2.5 py-1 text-xs font-semibold text-gray-100 hover:bg-gray-500 transition-colors"
                    >
                      {t('editor:storyboardTab.inlineActions.sizeUpMore')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUpdateSpanFontSize(span.id, undefined)}
                      className="rounded-md bg-gray-600 px-2.5 py-1 text-xs text-gray-200 hover:bg-gray-500 transition-colors"
                    >
                      {t('editor:storyboardTab.inlineActions.resetSize')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteSpan(span.id)}
                      className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                      title={t('common:deleteResource')}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
